import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { formatDateTime } from '@belong/common';
import { Text, Toast } from '@belong/ui';
import {
  EmployeeAssignmentDetailModel,
  MaintenanceRecurrenceDetailModel,
} from 'api/models';
import Grid from 'components/Grid/Grid';
import { MaintenanceHomecareSpecialist } from 'containercomponents/MaintenanceStatus/MaintenanceHomecareSpecialist/MaintenanceHomecareSpecialist';
import { startCase } from 'es-toolkit/compat';
import { URL_QUERY_PARAMS } from 'maintenance/constants';
import { STRINGS } from 'maintenance/strings';
import {
  getFormattedFrequency,
  recurringTasksHomeowner,
  residentTasks,
} from 'maintenance/utils';
import { MaintenanceRecurrenceApprovalResponsibility } from 'pages/Accounts/Components/maintenance-recurrence/components/maintenance-recurrence-approval-responsibility';
import { MaintenanceRecurrenceHeader } from 'pages/Accounts/Components/maintenance-recurrence/components/maintenance-recurrence-header';
import { MaintenanceRecurrenceNavBar } from 'pages/Accounts/Components/maintenance-recurrence/components/maintenance-recurrence-nav-bar';
import { MaintenanceRecurrenceResidentNotice } from 'pages/Accounts/Components/maintenance-recurrence/components/maintenance-recurrence-resident-notice';
import { MaintenanceRecurrenceSubtasksList } from 'pages/Accounts/Components/maintenance-recurrence/components/maintenance-recurrence-subtasks-list';
import {
  getFormattedAddress,
  getRecurrenceActivatedByUser,
} from 'pages/Accounts/Components/maintenance-recurrence/maintenance-recurrence.utils';
import { fetchEmployees } from 'store/redux/maintenance/actions';
import { MaintenanceRecurrenceNotes } from './components/maintenance-recurrence-notes';

type MaintenanceRecurrenceProps = {
  // Nullable to match the hook that supplies it; the body already reads it
  // through `?.`.
  task: MaintenanceRecurrenceDetailModel | null;
  onSubtaskClick: (subtask: any) => void;
  onClosePress: () => void;
  isResident?: boolean;
};

export const MaintenanceRecurrence = ({
  task,
  onSubtaskClick,
  onClosePress,
  isResident = false,
}: MaintenanceRecurrenceProps) => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { replace } = useHistory();
  const [homecareSpecialist, setHomecareSpecialist] = useState<
    EmployeeAssignmentDetailModel | undefined
  >();
  const [isToastVisible, setIsToastVisible] = useState(false);
  const taskIdentifier = task?.maintenanceRecurrence.id;

  const fetchHomecareSpecialist = useCallback(async () => {
    try {
      if (taskIdentifier) {
        const _homecareSpecialist = await dispatch(
          fetchEmployees(
            'MaintenanceRecurrence',
            taskIdentifier,
            'HomecareSpecialist',
          ),
        );
        setHomecareSpecialist(_homecareSpecialist);
      }
    } catch (e: any) {
      console.log(e);
      if (e.title === 'Not Found') {
        try {
          if (task?.home?.id) {
            const _homecareSpecialist = await dispatch(
              fetchEmployees('Home', task.home.id, 'HomecareSpecialist'),
            );
            setHomecareSpecialist(_homecareSpecialist);
          }
        } catch (err) {
          console.log(err);
        }
      }
    }
  }, [dispatch, taskIdentifier, task?.home?.id]);

  const getToastText = () => {
    const parsedURL = new URLSearchParams(search);

    const cancelURLParam = parsedURL.get(URL_QUERY_PARAMS.CANCEL);
    const nextOnURLParam = parsedURL.get(URL_QUERY_PARAMS.NEXT_ON);
    const taskURLParam = parsedURL.get(URL_QUERY_PARAMS.TASK);

    let toastText = '';

    if (cancelURLParam) {
      toastText = STRINGS['single-visit-cancelled'];
    }

    if (cancelURLParam && nextOnURLParam) {
      const taskGroup = isResident ? residentTasks : recurringTasksHomeowner;
      const formattedTask = taskURLParam
        ? Object.values(taskGroup)
            .flat()
            .find(
              (taskItem) =>
                taskItem.key?.toLowerCase() === taskURLParam.toLowerCase(),
            )?.label
        : undefined;

      const formattedDate = formatDateTime({
        dateTime: nextOnURLParam,
        format: 'MM/dd/yy',
      });

      toastText = `${STRINGS['single-visit-cancelled-date']} ${formattedTask} ${STRINGS.around} ${formattedDate}.`;
    }

    return toastText;
  };

  const handleToastClose = () => {
    setIsToastVisible(false);

    replace(pathname);
  };

  useEffect(() => {
    const parsedURL = new URLSearchParams(search);

    const toastVisibleURLParam = parsedURL.get(URL_QUERY_PARAMS.TOAST_VISIBLE);

    setIsToastVisible(toastVisibleURLParam === 'true');
  }, [search]);

  useEffect(() => {
    if (taskIdentifier) {
      fetchHomecareSpecialist();
    }
  }, [fetchHomecareSpecialist, taskIdentifier]);

  const scheduleHomecareSpecialistCall = () => {
    if (homecareSpecialist?.employee?.calendarLink) {
      const formattedLink = homecareSpecialist.employee.calendarLink.includes(
        'https',
      )
        ? homecareSpecialist.employee.calendarLink
        : `https://${homecareSpecialist.employee.calendarLink}`;
      return window.open(formattedLink, '_blank');
    }
  };

  return (
    <>
      <div className="flex w-full flex-col items-center">
        <div className="w-full">
          <MaintenanceRecurrenceNavBar
            onClosePress={onClosePress}
            isResident={isResident}
            task={task}
          />
        </div>
        <div className="px-2sm sm:max-w-[730px] sm:px-0">
          {!!task && (
            <Grid extraPadding className="p-0">
              <MaintenanceRecurrenceHeader
                title={startCase(task.maintenanceRecurrence.summary)}
                frequency={getFormattedFrequency(
                  task.maintenanceRecurrence.repeatFrequencyDays,
                )}
                homeAddress={
                  task.home?.address && getFormattedAddress(task.home.address)
                }
                maintenanceId={task.maintenanceRecurrence.id}
                status={
                  task.maintenanceRecurrence.isCancelled
                    ? 'TurnedOff'
                    : undefined
                }
              />

              <div className="my-2xl">
                <Text fontWeight="semibold" variant="h3">
                  Summary
                </Text>
                <div className="mt-xs">
                  <Text>{task.maintenanceRecurrence.description}</Text>
                </div>
                {isResident &&
                  task.maintenanceRecurrence.requestedBy === 'Homeowner' && (
                    <div className="mt-xs">
                      <MaintenanceRecurrenceResidentNotice
                        isRequestedByHomeowner
                        homeownerName={task.homeowner.firstName}
                        frequency={getFormattedFrequency(
                          task.maintenanceRecurrence.repeatFrequencyDays,
                        )}
                      />
                    </div>
                  )}
              </div>

              {homecareSpecialist && (
                <div className="mt-md">
                  <MaintenanceHomecareSpecialist
                    homecareSpecialist={homecareSpecialist}
                    onScheduleACallPress={() =>
                      scheduleHomecareSpecialistCall()
                    }
                  />
                </div>
              )}

              <MaintenanceRecurrenceNotes task={task} isResident={isResident} />

              {task.subTasks?.length > 0 && (
                <MaintenanceRecurrenceSubtasksList
                  subtasks={task.subTasks}
                  onSubtaskClick={onSubtaskClick}
                />
              )}

              <MaintenanceRecurrenceApprovalResponsibility
                userRole={task.maintenanceRecurrence.requestedBy}
                isActive={!task.maintenanceRecurrence.isCancelled}
                date={
                  task.maintenanceRecurrence.isCancelled
                    ? task.maintenanceRecurrence.endOn
                    : task.maintenanceRecurrence.createdOn
                }
                user={task && getRecurrenceActivatedByUser(task)}
              />
            </Grid>
          )}
        </div>
      </div>
      <Toast isVisible={isToastVisible} onClose={handleToastClose}>
        <div className="sm:max-w-[530px]">{getToastText()}</div>
      </Toast>
    </>
  );
};
